import { useState } from "react";

const MIN_PASSWORD_LENGTH = 6;

/**
 * A person changing their own password.
 *
 * Asks for the current one, unlike the admin reset on the Users screen: an
 * admin resetting a forgotten password does not have it, but someone changing
 * their own does — and requiring it is what stops an unattended session being
 * used to take the account over.
 */
export function ChangePassword({ onClose }: { onClose: () => void }) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const mismatch = confirm.length > 0 && next !== confirm;
  const tooShort = next.length > 0 && next.length < MIN_PASSWORD_LENGTH;
  const canSubmit =
    current.length > 0 && next.length >= MIN_PASSWORD_LENGTH && next === confirm && !saving;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;

    try {
      setSaving(true);
      setError(null);
      const res = await fetch("/api/v1/auth/change-password", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ currentPassword: current, newPassword: next }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.error || "Failed to change password");

      setDone(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to change password");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal-card" onClick={(e) => e.stopPropagation()}>
        <h2 style={{ marginBottom: 4 }}>Change password</h2>

        {done ? (
          <>
            <p className="muted" style={{ marginTop: 0 }}>
              Your password has been updated. Your current session stays signed in.
            </p>
            <div className="row" style={{ justifyContent: "flex-end" }}>
              <button className="btn primary" onClick={onClose}>
                Done
              </button>
            </div>
          </>
        ) : (
          <form onSubmit={submit}>
            <div className="field">
              <label>Current password</label>
              <input
                type="password"
                required
                value={current}
                onChange={(e) => setCurrent(e.target.value)}
                autoFocus
              />
            </div>

            <div className="field">
              <label>New password</label>
              <input
                type="password"
                required
                minLength={MIN_PASSWORD_LENGTH}
                placeholder={`At least ${MIN_PASSWORD_LENGTH} characters`}
                value={next}
                onChange={(e) => setNext(e.target.value)}
              />
              {tooShort && (
                <span className="mini" style={{ color: "var(--red)" }}>
                  Must be at least {MIN_PASSWORD_LENGTH} characters.
                </span>
              )}
            </div>

            <div className="field">
              <label>Confirm new password</label>
              <input
                type="password"
                required
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
              />
              {mismatch && (
                <span className="mini" style={{ color: "var(--red)" }}>
                  The two passwords do not match.
                </span>
              )}
            </div>

            {error && <div className="notice error">{error}</div>}

            <div className="row" style={{ justifyContent: "flex-end", gap: 8, marginTop: 12 }}>
              <button type="button" className="btn" onClick={onClose}>
                Cancel
              </button>
              <button type="submit" className="btn primary" disabled={!canSubmit}>
                {saving ? "Saving…" : "Change password"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
